import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { LogOut, Plus, Calendar, Clock, Pill, FileText, Upload, Loader2, Paperclip, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { PrescriptionChat } from "@/components/PrescriptionChat";

const statusColor = (s) =>
  s === "approved" ? "bg-champagne-gold text-deep-ink"
  : s === "rejected" ? "bg-deep-ink/10 text-deep-ink/50"
  : s === "submitted" ? "bg-muted-sage/30 text-deep-ink"
  : "bg-terracotta/15 text-terracotta";

export default function PatientPortal() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [appts, setAppts] = useState([]);
  const [rx, setRx] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ medication: "", dosage: "", reason: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chatOpen, setChatOpen] = useState(null);

  useEffect(() => {
    api.get("/patient/appointments").then(({ data }) => setAppts(data)).catch(() => {});
    api.get("/prescriptions").then(({ data }) => setRx(data)).catch(() => {});
  }, []);

  const fmtFull = (iso) => { try { return new Date(iso).toLocaleString(i18n.language, { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; } };

  const now = Date.now();
  const upcoming = appts.filter((a) => new Date(a.slot).getTime() >= now).sort((a, b) => new Date(a.slot) - new Date(b.slot));
  const past = appts.filter((a) => new Date(a.slot).getTime() < now).sort((a, b) => new Date(b.slot) - new Date(a.slot));

  const submit = async () => {
    if (!form.medication.trim()) { toast.error(t("portal.rx.medication")); return; }
    setBusy(true);
    try {
      let documentId = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        documentId = data.id;
      }
      const { data } = await api.post("/prescriptions", { ...form, document_id: documentId });
      setRx((r) => [data, ...r]);
      setOpen(false); setForm({ medication: "", dosage: "", reason: "" }); setFile(null);
      toast.success(t("portal.rx.success"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("portal.errors.generic"));
    } finally { setBusy(false); }
  };

  const viewDoc = async (fileId) => {
    try {
      const res = await fetch(`${API}/files/${fileId}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error(t("portal.errors.generic")); }
  };

  return (
    <div className="min-h-screen bg-sand" data-testid="patient-portal">
      <Toaster position="top-center" />
      <header className="flex items-center justify-between border-b border-border bg-warm-ivory px-5 py-4 lg:px-10">
        <Link to="/"><Logo /></Link>
        <div className="flex items-center gap-4">
          <span className="hidden text-sm text-deep-ink/60 sm:inline">{t("portal.nav.welcome")}, {user?.name || user?.email}</span>
          <Button data-testid="portal-logout" variant="ghost" onClick={() => { logout(); navigate("/portal/login"); }} className="gap-2 text-deep-ink">
            <LogOut className="h-4 w-4" />{t("portal.nav.logout")}
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-5 py-8 lg:px-10">
        <h1 className="font-serif text-3xl text-deep-ink lg:text-4xl">{t("portal.title")}</h1>
        <p className="mt-2 text-sm text-deep-ink/60">{t("portal.disclaimer")}</p>

        <Tabs defaultValue="appointments" className="mt-8">
          <TabsList className="bg-warm-ivory">
            <TabsTrigger value="appointments" data-testid="tab-appointments">{t("portal.nav.appointments")}</TabsTrigger>
            <TabsTrigger value="prescriptions" data-testid="tab-prescriptions">{t("portal.nav.prescriptions")}</TabsTrigger>
          </TabsList>

          <TabsContent value="appointments" className="mt-6">
            {appts.length === 0 ? (
              <div className="rounded-3xl bg-warm-ivory p-10 text-center">
                <p className="text-deep-ink/60">{t("portal.appt.empty")}</p>
                <Button asChild className="mt-4 rounded-full bg-deep-ink text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink"><Link to="/book">{t("portal.appt.book")}</Link></Button>
              </div>
            ) : (
              <div className="space-y-6">
                {[["upcoming", upcoming], ["past", past]].map(([key, items]) => items.length > 0 && (
                  <div key={key}>
                    <p className="mb-2 text-xs uppercase tracking-editorial text-deep-ink/50">{t(`portal.appt.${key}`)}</p>
                    <div className="space-y-2">
                      {items.map((a) => (
                        <div key={a.id} data-testid={`portal-appt-${a.reference}`} className="flex items-center justify-between gap-4 rounded-2xl bg-warm-ivory p-4">
                          <div>
                            <p className="flex items-center gap-2 text-sm font-medium text-deep-ink"><Calendar className="h-4 w-4 text-champagne-gold" />{fmtFull(a.slot)}</p>
                            <p className="mt-1 text-xs text-deep-ink/50">{a.format === "online" ? "Online" : "In person"} · {a.reference}</p>
                          </div>
                          <Badge className={statusColor(a.status === "confirmed" ? "approved" : "submitted")}>{a.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="prescriptions" className="mt-6">
            <div className="mb-4 flex justify-end">
              <Button onClick={() => setOpen(true)} data-testid="new-rx" className="gap-2 rounded-full bg-champagne-gold text-deep-ink hover:bg-deep-ink hover:text-warm-ivory">
                <Plus className="h-4 w-4" />{t("portal.rx.newBtn")}
              </Button>
            </div>
            {rx.length === 0 ? (
              <div className="rounded-3xl bg-warm-ivory p-10 text-center text-deep-ink/60">{t("portal.rx.empty")}</div>
            ) : (
              <div className="space-y-3">
                {rx.map((p) => (
                  <div key={p.id} data-testid={`portal-rx-${p.id}`} className="rounded-2xl bg-warm-ivory p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="flex items-center gap-2 font-serif text-lg text-deep-ink"><Pill className="h-4 w-4 text-champagne-gold" />{p.medication}</p>
                        {p.dosage && <p className="mt-0.5 text-sm text-deep-ink/60">{p.dosage}</p>}
                        {p.reason && <p className="mt-1 text-sm text-deep-ink/70">{p.reason}</p>}
                        <p className="mt-2 text-xs text-deep-ink/40">{t("portal.rx.requested")}: {fmtDate(p.created_at)}</p>
                      </div>
                      <Badge className={statusColor(p.status)} data-testid={`rx-status-${p.id}`}>{t(`portal.status.${p.status}`)}</Badge>
                    </div>
                    {p.status_note && <p className="mt-3 rounded-xl bg-sand p-3 text-sm text-deep-ink/80"><span className="text-deep-ink/40">{t("portal.rx.note")}: </span>{p.status_note}</p>}
                    {p.document_id && (
                      <button onClick={() => viewDoc(p.document_id)} data-testid={`rx-doc-${p.id}`} className="mt-3 inline-flex items-center gap-1.5 text-sm text-champagne-gold hover:underline">
                        <Paperclip className="h-3.5 w-3.5" />{t("portal.rx.viewDoc")}
                      </button>
                    )}
                    <div className="mt-3 border-t border-border/60 pt-3">
                      <button onClick={() => setChatOpen(chatOpen === p.id ? null : p.id)} data-testid={`rx-chat-toggle-${p.id}`} className="inline-flex items-center gap-1.5 text-sm text-deep-ink/70 hover:text-champagne-gold">
                        <MessageCircle className="h-3.5 w-3.5" />{t("portal.rx.messages")}
                      </button>
                      {chatOpen === p.id && <div className="mt-2"><PrescriptionChat prescriptionId={p.id} /></div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="rx-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-deep-ink">{t("portal.rx.formTitle")}</DialogTitle>
            <DialogDescription className="text-sm text-deep-ink/60">{t("portal.disclaimer")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-sm text-deep-ink/70">{t("portal.rx.medication")}</Label>
              <Input data-testid="rx-medication" value={form.medication} onChange={(e) => setForm({ ...form, medication: e.target.value })} placeholder={t("portal.rx.medicationPlaceholder")} className="bg-sand" />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm text-deep-ink/70">{t("portal.rx.dosage")}</Label>
              <Input data-testid="rx-dosage" value={form.dosage} onChange={(e) => setForm({ ...form, dosage: e.target.value })} placeholder={t("portal.rx.dosagePlaceholder")} className="bg-sand" />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm text-deep-ink/70">{t("portal.rx.reason")}</Label>
              <Textarea data-testid="rx-reason" rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder={t("portal.rx.reasonPlaceholder")} className="bg-sand" />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm text-deep-ink/70">{t("portal.rx.document")}</Label>
              <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-dashed border-border bg-sand p-4 text-sm text-deep-ink/70 hover:border-champagne-gold">
                <Upload className="h-4 w-4 text-champagne-gold" />
                <span data-testid="rx-file-label">{file ? file.name : t("portal.rx.upload")}</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="rx-file"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
              <p className="mt-1 text-xs text-deep-ink/40">{t("portal.rx.uploadHint")}</p>
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button onClick={submit} disabled={busy} data-testid="rx-submit" className="w-full gap-2 rounded-full bg-deep-ink text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              {busy ? t("portal.rx.submitting") : t("portal.rx.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
